import { supabase } from "./supabaseClient";

export type GuestSession = { token: string; userId: string; isAnonymous: boolean };

let inFlight: Promise<GuestSession | null> | null = null;

/**
 * Ensure a durable guest session (silent anonymous sign-in) and return the
 * access token to send on room joins. Returns `null` when Supabase isn't
 * configured or sign-in fails — the caller then sends the legacy guest id and
 * the server's onAuth falls back accordingly. Foundations F1.
 *
 * supabase-js persists + auto-refreshes the session in localStorage, so the
 * anonymous account (and its user id) is stable across reloads — the seam a
 * wallet/email login binds to later, with no migration.
 */
export function ensureGuestSession(): Promise<GuestSession | null> {
  // One sign-in at a time: a first visit's boot asks from several places at
  // once (social, the hub room, the account sync), and each used to see no
  // session and sign up, making three guests; the saves then went out under
  // one user's token for another's row and RLS refused them.
  inFlight ??= resolveGuestSession().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function resolveGuestSession(): Promise<GuestSession | null> {
  if (!supabase) return null;
  try {
    let session = (await supabase.auth.getSession()).data.session;
    if (!session) {
      const created = await supabase.auth.signInAnonymously();
      if (created.error) {
        console.warn("[auth] anonymous sign-in failed:", created.error.message);
        return null;
      }
      session = created.data.session;
    }
    if (!session) return null;
    return { token: session.access_token, userId: session.user.id, isAnonymous: session.user.is_anonymous ?? true };
  } catch (e) {
    console.warn("[auth] ensureGuestSession error:", e instanceof Error ? e.message : e);
    return null;
  }
}
